import React, { useEffect, useState } from "react";
import axios from "axios";
import { LicenseOrderForm } from "@/components/LicenseOrderForm";
import { OrderStatusPill } from "@/components/LicenseOrdersAdmin";
import { API, useAuth, formatApiError } from "@/auth";
import { KeyRound, ShieldCheck, Timer, Loader2 } from "lucide-react";

export default function LicencesPage() {
  const { user, refreshMe } = useAuth();
  const [key, setKey] = useState("");
  const [loading, setLoading] = useState(false);
  const [msg, setMsg] = useState(null);

  const activate = async (e) => {
    e.preventDefault(); setLoading(true); setMsg(null);
    try {
      await axios.post(`${API}/licenses/activate`, { key });
      await refreshMe();
      setMsg({ ok: true, text: "Licence activée avec succès." });
      setKey("");
    } catch (err) {
      setMsg({ ok: false, text: formatApiError(err) });
    } finally { setLoading(false); }
  };

  const expDays = user?.license_expires_at
    ? Math.max(0, Math.ceil((new Date(user.license_expires_at) - new Date()) / (1000 * 60 * 60 * 24)))
    : 0;

  return (
    <div className="p-4 sm:p-6 space-y-6" data-testid="licences-page">
      <div className="card-lhs p-5 sm:p-6">
        <div className="flex flex-col lg:flex-row items-start lg:items-center justify-between gap-4">
          <div>
            <div className="text-xs text-zinc-500 uppercase tracking-widest font-mono-tech">Ma licence</div>
            <h1 className="font-display text-2xl sm:text-3xl font-extrabold mt-1">Gestion des licences</h1>
            <p className="text-zinc-400 text-sm mt-2">
              Activez une clé pour débloquer les fonctions professionnelles de LHSunlocker.
            </p>
          </div>
          <div className="grid grid-cols-3 gap-2 sm:gap-4 text-center w-full lg:w-auto">
            <StatMini icon={ShieldCheck} label="Statut" value={user?.license_status === "active" ? "ACTIVE" : "INACTIVE"} color={user?.license_status === "active" ? "text-emerald-400" : "text-red-400"} />
            <StatMini icon={KeyRound} label="Formule" value={user?.license_plan || "—"} />
            <StatMini icon={Timer} label="Jours restants" value={user?.license_status === "active" ? expDays : "—"} color="text-orange-400" />
          </div>
        </div>
      </div>

      <div className="card-lhs p-5 sm:p-6">
        <h2 className="font-display font-bold uppercase tracking-wider text-sm mb-4">Activer une clé de licence</h2>
        <form onSubmit={activate} className="flex flex-col sm:flex-row gap-3 sm:max-w-2xl">
          <input
            data-testid="license-key-input"
            value={key}
            onChange={(e) => setKey(e.target.value)}
            required
            placeholder="LHS-XXXX-XXXX-XXXX-XXXX"
            className="input-lhs font-mono-tech uppercase flex-1"
          />
          <button data-testid="license-activate-btn" disabled={loading} className="btn-orange px-5 py-3 sm:py-2 rounded-lg text-sm uppercase tracking-wider flex items-center justify-center gap-2">
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <KeyRound className="w-4 h-4" />}
            Activer
          </button>
        </form>
        {msg && (
          <div data-testid="license-msg" className={`mt-3 text-sm px-3 py-2 rounded-lg border ${
            msg.ok ? "text-emerald-400 border-emerald-500/40 bg-emerald-500/10"
                   : "text-red-400 border-red-500/40 bg-red-500/10"
          }`}>{msg.text}</div>
        )}
      </div>

      <OrderSection user={user} />
    </div>
  );
}

function StatMini({ icon: Icon, label, value, color = "text-white" }) {
  return (
    <div className="card-lhs px-2 sm:px-4 py-3">
      <div className="flex items-center gap-1 sm:gap-2 justify-center text-[9px] sm:text-[10px] uppercase text-zinc-500 tracking-wider font-mono-tech">
        <Icon className="w-3 h-3" /> {label}
      </div>
      <div className={`font-display font-extrabold text-base sm:text-lg mt-1 ${color}`}>{value}</div>
    </div>
  );
}

function OrderSection({ user }) {
  const [open, setOpen] = useState(false);
  const [mine, setMine] = useState([]);
  useEffect(() => { axios.get(`${API}/license-orders/mine`).then(r => setMine(r.data)).catch(() => {}); }, [open]);
  return (
    <div className="card-lhs p-5 sm:p-6 space-y-5" data-testid="licences-order-section">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h2 className="font-display font-bold uppercase tracking-wider text-sm">Commander une licence</h2>
          <p className="text-xs text-zinc-400 mt-1">LHSunlocker ou autre logiciel · le prix et la durée sont confirmés par l'administrateur.</p>
        </div>
        {!open && (
          <button onClick={() => setOpen(true)} data-testid="licences-order-btn" className="btn-orange rounded-lg px-5 py-2.5 text-sm uppercase tracking-wider">
            Commander
          </button>
        )}
      </div>
      {open && <LicenseOrderForm defaults={{ username: user?.name, email: user?.email }} />}
      {mine.length > 0 && (
        <div className="space-y-2" data-testid="my-orders">
          <div className="text-[11px] uppercase text-zinc-500 font-mono-tech">Mes commandes</div>
          {mine.map(o => (
            <div key={o.id} className="flex items-center justify-between gap-2 flex-wrap rounded-lg border border-zinc-800 bg-[#0C0C10] p-3 text-xs">
              <span className="font-mono-tech text-orange-400">{o.reference}</span>
              <span className="text-zinc-300">{o.software}</span>
              <span className="text-zinc-500">{new Date(o.created_at).toLocaleDateString("fr-FR")}</span>
              <OrderStatusPill s={o.status} />
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
